/**
 * KPI Export Utility
 * Supports: Excel (.xlsx), PDF (.pdf), Word (.docx)
 *
 * Each section passed in follows this shape:
 * {
 *   title:   string,          // e.g. "1. Trips per Driver Hour"
 *   period:  string,          // e.g. "February 2026"
 *   stats:   [{label, value}] // summary stat boxes
 *   chartId: string           // id of chart wrapper div (optional, for PDF/Word)
 *   headers: [string]         // table column headers (Excel only; optional)
 *   rows:    [[...]]          // table rows (Excel only; optional)
 * }
 *
 * PDF/Word: stats summary + chart image (no raw data tables)
 * Excel:    stats summary + all detail rows
 */

// ─── EXCEL ────────────────────────────────────────────────────────────────────
export async function exportToExcel(sections, filename = 'KPI_Report') {
  const XLSX = await import('xlsx');

  const wb = XLSX.utils.book_new();

  sections.forEach((sec) => {
    const data = [];

    // Title row
    data.push([sec.title]);
    data.push([`Period: ${sec.period}`]);
    data.push([]);

    // Stats
    if (sec.stats?.length) {
      data.push(['Summary']);
      sec.stats.forEach((s) => data.push([s.label, s.value]));
      data.push([]);
    }

    // Table
    if (sec.headers?.length) {
      data.push(sec.headers);
      (sec.rows || []).forEach((r) => data.push(r));
    }

    const ws = XLSX.utils.aoa_to_sheet(data);

    // Column widths
    ws['!cols'] = [{ wch: 30 }, ...(sec.headers || []).slice(1).map(() => ({ wch: 18 }))];

    // Sheet name — Excel limits to 31 chars, strip special chars
    const sheetName = sec.title.replace(/[:\\/?*[\]]/g, '').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// ─── chart capture helper (SVG → PNG data URL) ──────────────────────────────
async function captureChartImage(chartId) {
  if (!chartId) return null;
  try {
    const el = document.getElementById(chartId);
    if (!el) return null;
    const svg = el.querySelector('svg');
    if (!svg) return null;
    const { width: w, height: h } = svg.getBoundingClientRect();
    if (!w || !h) return null;
    const clone = svg.cloneNode(true);
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const svgStr = new XMLSerializer().serializeToString(clone);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    await new Promise((res) => {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0); res(); };
      img.onerror = () => res();
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
    });
    return canvas.toDataURL('image/png');
  } catch { return null; }
}

// ─── PDF (stats + chart image) ────────────────────────────────────────────────
export async function exportToPdf(sections, filename = 'KPI_Report') {
  const { default: jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const MARGIN = 14;
  const COLS = 4;
  const STAT_H = 24;
  const STAT_GAP = 3;
  const STAT_W = (pageW - MARGIN * 2 - (COLS - 1) * STAT_GAP) / COLS;

  const drawHeaderBar = (period) => {
    doc.setFillColor(70, 128, 255);
    doc.rect(0, 0, pageW, 11, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('SOL Transportation — KPI Report', MARGIN, 7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(period, pageW - MARGIN, 7.5, { align: 'right' });
  };

  // Adaptive font size: shrink if value string is long
  const valueFontSize = (val) => {
    const s = String(val ?? '—');
    if (s.length <= 6)  return 13;
    if (s.length <= 9)  return 11;
    if (s.length <= 12) return 9;
    return 8;
  };

  for (const [idx, sec] of sections.entries()) {
    if (idx > 0) doc.addPage();
    drawHeaderBar(sec.period);

    let y = 20;

    // Section title
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(sec.title, MARGIN, y);
    y += 6;

    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y, pageW - MARGIN, y);
    y += 6;

    // Stats grid (4 per row)
    if (sec.stats?.length) {
      sec.stats.forEach((s, i) => {
        const col = i % COLS;
        const row = Math.floor(i / COLS);
        const x = MARGIN + col * (STAT_W + STAT_GAP);
        const sy = y + row * (STAT_H + STAT_GAP);

        // Card background
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, sy, STAT_W, STAT_H, 3, 3, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, sy, STAT_W, STAT_H, 3, 3, 'S');

        // Value — adaptive font size based on string length
        const vSize = valueFontSize(s.value);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(vSize);
        doc.setTextColor(30, 41, 59);
        // Clip to card width with maxWidth
        doc.text(String(s.value ?? '—'), x + STAT_W / 2, sy + 10, {
          align: 'center',
          maxWidth: STAT_W - 4,
        });

        // Label
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.text(s.label, x + STAT_W / 2, sy + 18, {
          align: 'center',
          maxWidth: STAT_W - 4,
        });
      });

      const statRows = Math.ceil(sec.stats.length / COLS);
      y += statRows * (STAT_H + STAT_GAP) + 6;
    }

    // Chart image
    if (sec.chartId) {
      const imgData = await captureChartImage(sec.chartId);
      if (imgData) {
        const chartH = 65;
        if (y + chartH > pageH - 20) { doc.addPage(); drawHeaderBar(sec.period); y = 20; }
        doc.addImage(imgData, 'PNG', MARGIN, y, pageW - MARGIN * 2, chartH);
        y += chartH + 4;
      }
    }

    // Footer
    const genTime = new Date().toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated ${genTime}`, MARGIN, pageH - 6);
    doc.text('SOL Transportation — Confidential', pageW - MARGIN, pageH - 6, { align: 'right' });
  }

  doc.save(`${filename}.pdf`);
}

// ─── WORD (stats + chart image) ─────────────────────────────────────────────
export async function exportToWord(sections, filename = 'KPI_Report') {
  const {
    Document, Packer, Paragraph, Table, TableRow, TableCell,
    TextRun, HeadingLevel, AlignmentType, WidthType, ShadingType,
    BorderStyle, ImageRun,
  } = await import('docx');

  const BLUE = '4680FF';
  const STAT_BG = 'F8FAFC';
  const BORDER_COLOR = 'E2E8F0';

  const noBorder = {
    top:    { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    left:   { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right:  { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  };

  const children = [];

  // Cover title
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'SOL Transportation', bold: true, size: 48, color: BLUE })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 400, after: 120 },
    }),
    new Paragraph({
      children: [new TextRun({ text: 'KPI Report', bold: true, size: 36, color: '1E293B' })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: sections[0]?.period || '', size: 22, color: '64748B' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `Generated: ${new Date().toLocaleString()}`, size: 18, color: '94A3B8' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
    }),
  );

  for (const sec of sections) {
    // Section heading
    children.push(
      new Paragraph({
        children: [new TextRun({ text: sec.title, bold: true, size: 28, color: '1E293B' })],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 },
        border: {
          bottom: { style: BorderStyle.SINGLE, size: 6, color: BORDER_COLOR },
        },
      }),
    );

    // Stats as a 2-column label/value table (report style)
    if (sec.stats?.length) {
      // Split into pairs for a 2-column layout per row
      const STAT_COLS = 2;
      for (let i = 0; i < sec.stats.length; i += STAT_COLS) {
        const pair = sec.stats.slice(i, i + STAT_COLS);
        // Pad to STAT_COLS if odd
        while (pair.length < STAT_COLS) pair.push(null);

        const cells = pair.flatMap((s) => [
          // Label cell
          new TableCell({
            shading: { type: ShadingType.SOLID, color: STAT_BG, fill: STAT_BG },
            borders: noBorder,
            width: { size: 25, type: WidthType.PERCENTAGE },
            children: [new Paragraph({
              children: [new TextRun({ text: s?.label ?? '', size: 18, color: '64748B' })],
              spacing: { before: 60, after: 60 },
            })],
          }),
          // Value cell
          new TableCell({
            borders: noBorder,
            width: { size: 25, type: WidthType.PERCENTAGE },
            children: [new Paragraph({
              children: [new TextRun({ text: String(s?.value ?? ''), bold: true, size: 22, color: '1E293B' })],
              spacing: { before: 60, after: 60 },
            })],
          }),
        ]);

        children.push(new Table({
          rows: [new TableRow({ children: cells })],
          width: { size: 100, type: WidthType.PERCENTAGE },
        }));
      }
      children.push(new Paragraph({ spacing: { after: 200 } }));
    }

    // Chart image
    if (sec.chartId) {
      const imgData = await captureChartImage(sec.chartId);
      if (imgData) {
        const base64 = imgData.replace(/^data:image\/png;base64,/, '');
        children.push(
          new Paragraph({
            children: [new ImageRun({
              data: base64,
              transformation: { width: 550, height: 220 },
              type: 'png',
            })],
            spacing: { before: 160, after: 240 },
          }),
        );
      }
    }
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Dispatch by format ───────────────────────────────────────────────────────
export async function exportKpi(format, sections, filename, getIdToken = null) {
  const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
  const named = `${filename}_${ts}`;

  // Fire audit log best-effort
  if (getIdToken) {
    const { auditLog } = await import('./auditLog');
    auditLog(getIdToken, 'DOWNLOAD', 'REPORT', filename, {
      format,
      sections: sections.map(s => s.title),
      filename: named,
    });
  }

  if (format === 'excel') return exportToExcel(sections, named);
  if (format === 'pdf')   return exportToPdf(sections, named);
  if (format === 'word')  return exportToWord(sections, named);
}
